<template>
  <div id="services" class="compact">
    <PageHero title="CTO Advising" lede="Part-time technology leadership and advising for technology companies." />

    <section class="section">
      <div class="container">
        <div class="prose stack">
          <p class="lede">I advise and support senior technical and product
            leaders on decisions, analysis, process development, and staffing.
            You own the final decisions and the outcomes; my job is to make them
            better and faster.</p>
        </div>
      </div>
    </section>

    <section class="section tint">
      <div class="container">
        <div class="section-head">
          <span class="eyebrow">Where I help</span>
          <h2>Focus areas</h2>
        </div>
        <div class="masonry">
          <div class="card" v-for="(s, i) in areas" :key="s.title">
            <div class="num">{{ i + 1 }}</div>
            <h3>{{ s.title }}</h3>
            <p v-for="p in s.body" :key="p">{{ p }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="container">
        <div class="section-head">
          <span class="eyebrow">How it works</span>
          <h2>How an engagement runs</h2>
          <p class="lede">Four stages, each developed jointly with your product
            and engineering leadership.</p>
        </div>
        <ol class="steps">
          <li v-for="s in stages" :key="s.title">
            <h3>{{ s.title }} <small>{{ s.when }}</small></h3>
            <p>{{ s.text }}</p>
          </li>
        </ol>
      </div>
    </section>

    <section class="cta">
      <div class="container">
        <h2>Want a CTO in your corner?</h2>
        <p>Most engagements start with a conversation about where you are stuck.</p>
        <router-link to="/contact" class="btn light">Get in touch</router-link>
      </div>
    </section>
  </div>
</template>

<script>
import PageHero from '@/components/PageHero'

export default {
    name: 'services',
    title: 'CTO Advising - Ten Factor Growth',
    components: { PageHero },
    data() {
        return {
            areas: [
                { title: 'Technology and Data Strategy', body: [
                    "A company's technology and data strategies must evolve as it grows. I review your infrastructure, architecture, processes, and staff, find the bottlenecks slowing execution, and help you put the right level in place for your current stage."
                ] },
                { title: 'Becoming an AI-First Organization', body: [
                    'Most companies adopt AI first in engineering. An AI-first, or agentic, organization uses it across the cross-functional teams that deliver customer software, so people work at the requirements, architecture, and control level while agents help with coding, detailed requirements, test planning, and priority review.',
                    'I help you define the work model, the process that runs on it, and the organizational memory that staff and agents share, and introduce them incrementally, starting with smaller teams and growing from what you learn. That includes how third-party agent tools fit with or replace your existing infrastructure.'
                ] },
                { title: 'Product Development and Market Fit', body: [
                    "For the non-technical founder the selection, architecture, and development of a product can be daunting. It is relatively easy to build a first prototype, but building a complete product that meets stringent quality, performance, uptime, and user experience goals is much harder, and the wrong choices early can sink the company.",
                    'With over 30 years of technology experience, I have designed dozens of software and hardware systems, and I help teams map product, pricing, positioning, and messaging to the right customers at each stage of market engagement.'
                ] },
                { title: 'Operational Process and Team', body: [
                    'As start-ups move from seed to early growth, many hit process and communications issues. The shift from a team where everyone talks directly to one of written, process driven communication takes time, training, and sometimes new management.',
                    'I have taken multiple technology and product teams through this change. They end up with better morale, better results, and clearer accountability.'
                ] }
            ],
            stages: [
                { title: 'Discovery', when: 'typically 1 to 2 months, about 20 hours a week',
                  text: 'Investigate current requirements, architecture, processes, and staff. This needs cooperation from engineering and product leadership and staff, with checkpoint discussions along the way.' },
                { title: 'Recommendations', when: '',
                  text: 'Recommend changes in architecture, process, staff, and systems, developed with your senior product and engineering staff and presented to senior leadership for approval.' },
                { title: 'Implementation support', when: 'typically 3 to 4 months, about 10 hours a week',
                  text: 'Coaching, research, and definitional support for senior engineering staff as they implement the recommendations, building the team\'s capabilities as they go.' },
                { title: 'Ongoing support', when: 'as desired, about 4 hours a week',
                  text: 'Answers to questions, support for decisions, and updates on emerging capabilities.' }
            ]
        }
    }
}
</script>
